import React, { useContext, useState, memo } from 'react';
import {
  Typography,
  makeStyles,
  Box,
  withStyles,
  Paper,
  MenuList,
  Popper,
  MenuItem,
  alpha,
  Collapse,
} from '@material-ui/core';
import { colors } from '../../styles/theme';
import { AssetsDashboardContext } from '../../contexts/assetsDashboardContext';
import { SettingsItem } from './SettingsSection';
import { Accordion } from '../Map/component/ControlsOverlay/VisibilityButton/Accordions';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { KeyboardArrowDownOutlined } from '@material-ui/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { LanguageContext } from '../../contexts/LanguageContext/languageContext';
const AccordionSummary = withStyles({
  root: {
    flexDirection: 'row',
    padding: '10px 12px',
    fontSize: 15,
    fontWeight: 500,
    minHeight: 0,
    color: colors.black0,
    WebkitTapHighlightColor: 'transparent',
    height: 18,
    '&$expanded': {
      minHeight: 0,
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
      gap: 10,
      minHeight: 0,
      alignItems: 'center',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      marginLeft: 0,
      minHeight: 0,
    },
    '& .MuiIconButton-root': {
      padding: 4,
    },
    '& .MuiIconButton-edgeEnd': {
      marginRight: 0,
    },
  },
  expandIcon: {
    '&$expanded': {
      transform: 'rotate(180deg)',
    },
  },
  content: {
    marginLeft: 5,
    '&$expanded': {
      margin: '1px 0',
      marginLeft: 5,
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles(theme => ({
  root: {
    padding: 0,
    display: 'flex',
    fontSize: 15,
    fontWeight: 400,
    flexDirection: 'column',
    transition: 'all 300ms ease-in-out',
  },
}))(MuiAccordionDetails);

const NavigationItem = ({ menu, index }: { menu: SettingsItem; index: number }) => {
  const classes = useContentStyles();
  const { translate } = useContext(LanguageContext);
  const { isMobileView, openDrawer } = useContext(AssetsDashboardContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(
    openDrawer && !isMobileView ? { [menu.title]: true } : {}
  );
  const hasValidSubMenu = !!(menu?.subMenu && menu.subMenu.length > 0);

  const handleMenuClick = (e: React.MouseEvent<SVGSVGElement, MouseEvent>, menuName: string) => {
    e.stopPropagation();
    setOpenMenus(f => {
      const value = f[menuName];
      return { ...f, [menuName]: typeof value === 'boolean' ? !value : false };
    });
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  const RenderAccordion = ({ menu, menuKey }: { menu: SettingsItem; menuKey: string }) => {
    const hasValidSubMenu = !!(menu?.subMenu && menu.subMenu.length > 0);
    return (
      <Accordion
        key={menuKey}
        expanded={openMenus[menuKey]}
        defaultExpanded={!isMobileView}
        className={classes.accordion}
        TransitionComponent={Collapse}
        onClick={e => {
          e.stopPropagation();
          menu.clickHandler();
        }}>
        <AccordionSummary
          disableTouchRipple
          expandIcon={
            hasValidSubMenu && (
              <KeyboardArrowDownOutlined
                className={classes.expandIcon}
                onClick={e => {
                  handleMenuClick(e, menuKey);
                }}
              />
            )
          }>
          {menu.icon}
          <Typography className={classes.menuLabel}>{translate(menu.title)}</Typography>
        </AccordionSummary>
        {menu.subMenu && (
          <AccordionDetails>
            <Box className={classes.accordionDetails}>
              {menu.subMenu.map(menuItem => (
                <RenderAccordion key={menuItem.title} menu={menuItem} menuKey={menuKey + menuItem.title} />
              ))}
            </Box>
          </AccordionDetails>
        )}
      </Accordion>
    );
  };

  const RenderPopper = ({
    menuItems,
    anchorEl,
    menuKey,
    isMainMenu,
  }: {
    menuItems: SettingsItem[];
    anchorEl: null | HTMLElement;
    menuKey: string;
    isMainMenu: boolean;
  }) => {
    const [subMenuAnchor, setSubMenuAnchor] = useState<null | HTMLElement>(null);
    const [activeSubMenu, setActiveSubMenu] = useState<null | string>(null);
    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, subMenu: SettingsItem[] | undefined, menuTitle: string) => {
      if (subMenu && subMenu.length > 0) {
        setSubMenuAnchor(event.currentTarget);
        setActiveSubMenu(menuTitle);
      } else {
        setSubMenuAnchor(null);
        setActiveSubMenu(null);
      }
    };
    return (
      <Popper
        open={Boolean(anchorEl)} 
        anchorEl={anchorEl}
        placement="right-start"
        modifiers={{
          offset: {
            enabled: !isMainMenu ? true : false,
            offset: '0, 5',
          },
        }}>
        <Paper className={classes.popperMenu}>
          <MenuList className={classes.popperMenuContainer}>
            {menuItems.map(menu => {
              const hasValidSubMenu = !!(menu?.subMenu && menu.subMenu.length > 0);
              return (
                <Box key={menu.title}>
                  <MenuItem
                    className={clsx(classes.menuLabel, classes.popperMenuItem)}
                    onMouseEnter={e => {
                      e.stopPropagation();
                      handleMouseEnter(e, menu.subMenu, menu.title);
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      menu.clickHandler();
                    }}>
                    <Box className={classes.titleContainer}>
                      {menu.icon}
                      <Typography
                        className={clsx(classes.popperMenuTitle, hasValidSubMenu && classes.popperMenuRightGap)}>
                        {translate(menu.title)}
                      </Typography>
                    </Box>
                    {hasValidSubMenu && <FontAwesomeIcon icon={faChevronRight} className={classes.popperChevron} />}
                  </MenuItem>
                  {hasValidSubMenu && activeSubMenu === menu.title && (
                    <RenderPopper
                      menuItems={menu.subMenu!}
                      anchorEl={subMenuAnchor}
                      menuKey={menuKey + menu.title}
                      isMainMenu={false}
                    />
                  )}
                </Box>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>
    );
  };

  return (
    <>
      <Box
        key={menu.title}
        className={
          openDrawer
            ? classes.mobileMenuItem
            : `${classes.menuItem} ${index === 1 ? classes.menuItemRoot : ''} ${
                menu.isActive ? classes.activeMenuItem : ''
              }`
        }
        onClick={() => menu.clickHandler()}
        onMouseEnter={e => !openDrawer && handleMouseEnter(e)}
        onMouseLeave={() => !openDrawer && handleMouseLeave()}>
        {openDrawer ? (
          <RenderAccordion menu={menu} menuKey={menu.title} />
        ) : (
          <>
            {menu?.icon}
            <Typography
              className={
                openDrawer ? classes.menuText : `${classes.label} ${menu.isActive ? classes.activeLabel : ''}`
              }>
              {translate(menu.title)}
            </Typography>
          </>
        )}
        {hasValidSubMenu && (
          <RenderPopper menuItems={menu.subMenu!} anchorEl={anchorEl} menuKey={menu.title} isMainMenu />
        )}
      </Box>
    </>
  );
};

const useContentStyles = makeStyles(theme => ({
  item: {
    padding: 0,
    cursor: 'pointer',
    marginTop: 10,
  },
  itemText: {
    fontSize: 13,
    textTransform: 'uppercase',
    fontWeight: 500,
    color: theme.palette.primary.main,
    lineHeight: '13px',
  },
  itemIcon: { minWidth: 0, paddingRight: 5, marginBottom: 0.5 },

  menuItem: {
    width: 70,
    height: 70,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '6px',
    flexShrink: 0,
  },
  menuItemRoot: {
    gap: 2,
  },
  activeMenuItem: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '4px',
      backgroundColor: theme.palette.primary.main,
    },
  },
  menuIcon: {
    color: colors.black54,
  },
  label: {
    textAlign: 'center',
    fontSize: 12,
    fontFamily: 'Roboto',
    color: colors.black54,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  activeLabel: {
    color: theme.palette.primary.main,
  },
  mobileMenuItem: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  menuText: {
    color: colors.black75,
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 'normal',
  },
  expandIcon: {
    color: colors.title,
  },
  accordion: {
    width: '100%',
  },
  menuLabel: {
    color: colors.black0,
    textAlign: 'center',
    fontFamily: 'Roboto',
    fontSize: 15,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  accordionDetails: {
    marginLeft: 36,
  },
  popperMenuContainer: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  popperMenuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 8,
    '&:hover': {
      backgroundColor: `${alpha(theme.palette.primary.light, 0.1) || alpha(colors.defaultPrimary, 0.1)}`,
    },
  },
  popperMenuTitle: {
    fontSize: 14,
    fontWeight: 400,
    alignSelf: 'end',
  },
  popperMenuRightGap: {
    marginRight: 60,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    '& svg': {
      color: `${colors.black54} !important`,
    },
  },
  popperMenu: {
    borderRadius: 5,
    boxShadow: `0px 2px 8px 0px ${colors.black15}`,
  },
  popperChevron: {
    width: 8,
    height: 13,
    color: colors.title,
  },
}));
export default memo(NavigationItem);
